import { parseAamvaLicenseBarcode, type LicenseScanExtractedFields } from '@emr/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { prepareZXingModule, readBarcodes } from 'zxing-wasm/reader';
// Vite's `?url` suffix resolves to the built asset's own hashed URL rather than inlining or
// fetching from zxing-wasm's default jsDelivr CDN serve path — this app self-hosts the wasm binary
// like every other static asset, per `docs/decisions/patient-license-scan-scope.md` §1/§3's own
// "decode entirely client-side" design: nothing about a barcode scan should depend on a third-party
// CDN being reachable from the kiosk device.
//
// The specifier must match zxing-wasm's own package.json `exports` map exactly
// (`zxing-wasm/reader/zxing_reader.wasm`, no `dist/`) — a raw filesystem-shaped path
// (`zxing-wasm/dist/reader/zxing_reader.wasm`) is not itself a declared export and both Vite's dev
// server and a real `vite build` (Rolldown) refuse to resolve it, in both `development` and
// `production` mode. Found live: this page is a static import in `App.tsx`, so the failure poisoned
// every page's dev-server transform, not just this kiosk route, and broke the production build
// outright — confirmed by reverting to the wrong specifier and re-running both.
import zxingReaderWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

import { captureLicenseScan } from '../../api/licenseScan.ts';
import { ApiError, errorMessage } from '../../api/client.ts';
import { Button, ErrorText } from '../../components/primitives.tsx';
import { KioskShell } from './KioskCheckinPage.tsx';
import { useLicenseScanToken } from './useLicenseScanToken.ts';

prepareZXingModule({
  overrides: {
    locateFile: (path: string) => (path.endsWith('.wasm') ? zxingReaderWasmUrl : path),
  },
});

/**
 * The kiosk-side capture screen for a driver's-license barcode scan (`BACKLOG.md` C19,
 * `docs/decisions/patient-license-scan-scope.md`) — mounted at `/kiosk/license-scan`, outside
 * `RequireAuth` (`App.tsx`), the same "public route, different real auth" shape `KioskCheckinPage.tsx`
 * already establishes for `/kiosk`. There is no staff session anywhere in this file; every request
 * carries the `license_scan`-purpose bearer token this page read from its own URL fragment
 * (`useLicenseScanToken.ts`).
 *
 * **The entire barcode decode and AAMVA parse happen here, client-side, before anything reaches the
 * network.** `readBarcodes` (`zxing-wasm/reader`, an actively-maintained, MIT-licensed WASM build of
 * `zxing-cpp`) decodes a PDF417 symbol from a live camera frame; `parseAamvaLicenseBarcode`
 * (`@emr/shared`) turns the decoded text into the eight-field candidate shape. Only that structured
 * result is ever sent to `POST /api/license-scans/capture` — the video frames the decoder examines
 * are drawn to a throwaway `<canvas>` and never captured to a file, uploaded, or retained anywhere,
 * per the decision doc's §3 "it never arrives" retention answer.
 *
 * **No manual capture button.** The scan loop runs continuously against the live camera feed at a
 * fixed interval until a PDF417 symbol decodes into at least one usable field, then submits
 * automatically — matching the kiosk check-in flow's own "a tablet sitting in an exam room" framing:
 * the patient positions the license, nothing more to press.
 */
const SCAN_INTERVAL_MS = 350;

export function LicenseScanCapturePage() {
  const { t } = useTranslation('licenseScan');
  const token = useLicenseScanToken();

  if (token === null) {
    return <KioskShell title={t('noTokenTitle')} body={t('noTokenBody')} />;
  }

  return <LicenseScanFlow token={token} />;
}

function LicenseScanFlow({ token }: { readonly token: string }) {
  const { t } = useTranslation('licenseScan');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: (extractedFields: LicenseScanExtractedFields) =>
      captureLicenseScan(token, extractedFields),
  });
  // A ref, not a dependency, so the camera effect below mounts exactly once regardless of how many
  // times `submit` itself changes identity across renders (`useMutation` does not return a
  // reference-stable object) — restarting `getUserMedia` on every render would tear down and
  // re-request camera access repeatedly, which is both slow and a poor permission-prompt experience.
  const onDecodedRef = useRef<(fields: LicenseScanExtractedFields) => void>(() => {});
  // Updated in an effect, not during render — a ref may only be written outside of render (an
  // effect or an event handler), never as a direct assignment in the render body itself. No
  // dependency array: this keeps `onDecodedRef.current` pointing at the latest `submit` on every
  // render, which is the whole reason it exists as a ref instead of a plain closure variable.
  useEffect(() => {
    onDecodedRef.current = (fields) => {
      submit.mutate(fields);
    };
  });
  // Both assigned once, inside the camera effect below, and read from the separate submission-
  // outcome effect further down — two effects need to reach into the same running camera-loop
  // closure without becoming each other's dependency, which is exactly what a ref is for.
  const stopCameraRef = useRef<() => void>(() => {});
  const resumeScanningRef = useRef<() => void>(() => {});

  useEffect(() => {
    let stream: MediaStream | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;
    let decoding = false;
    // Paused, not stopped, while a candidate is mid-submission or already accepted — the camera
    // and decode loop themselves only ever stop for real (`stopCamera`, below) on unmount or a
    // genuine success, never merely because one submission attempt is in flight. A failed
    // submission un-pauses this via `resumeScanningRef` (the submission-outcome effect further
    // down), so "try again" resumes a live, already-running camera rather than a dead one.
    let paused = false;
    const canvas = document.createElement('canvas');

    // Stops the interval and releases the camera — called from the unmount cleanup and, via
    // `stopCameraRef`, from the submission-outcome effect once a capture genuinely succeeds. Never
    // called merely because one submission attempt started, so a failed attempt leaves the camera
    // running and `resumeScanning` below is all "try again" needs. Idempotent (`stream`/
    // `intervalId` are nulled after the first call).
    function stopCamera() {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      stream?.getTracks().forEach((track) => {
        track.stop();
      });
      stream = null;
    }
    function resumeScanning() {
      paused = false;
    }
    stopCameraRef.current = stopCamera;
    resumeScanningRef.current = resumeScanning;

    async function attemptDecode() {
      if (decoding || paused || cancelled) return;
      const video = videoRef.current;
      if (video === null || video.videoWidth === 0) return;

      decoding = true;
      try {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (context === null) return;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

        const results = await readBarcodes(imageData, {
          formats: ['PDF417'],
          tryHarder: true,
          maxNumberOfSymbols: 1,
        });
        const raw = results[0]?.text;
        // `videoRef.current`, not the `cancelled` closure boolean, is the post-`await` liveness
        // check — React nulls a ref out itself when the element it pointed at unmounts, so this is
        // a genuinely fresh read rather than a value TypeScript's control-flow analysis (incorrectly,
        // for an async closure another callback can mutate mid-`await`) would otherwise treat as
        // still narrowed to its pre-`await` value.
        if (raw === undefined || videoRef.current === null) return;

        const extractedFields = parseAamvaLicenseBarcode(raw);
        // A decoded symbol with none of the eight mapped fields (a damaged read, a non-AAMVA PDF417
        // barcode) is not a submission worth making — keep scanning silently rather than surfacing
        // every empty attempt as an error, per the decision doc's own "a barcode-decode failure is
        // not a dead end... simply means no scan assist is available" framing.
        if (Object.keys(extractedFields).length === 0) return;

        // Paused, not stopped — the submission-outcome effect decides whether this pause becomes
        // permanent (`stopCamera`, on success) or reverses (`resumeScanning`, on failure).
        paused = true;
        onDecodedRef.current(extractedFields);
      } catch {
        // A single frame failing to decode is the ordinary, expected case on almost every interval
        // tick — never surfaced, the loop just tries again on the next frame.
      } finally {
        decoding = false;
      }
    }

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
      } catch {
        if (!cancelled) setCameraError(t('cameraErrorBody'));
        return;
      }
      if (cancelled || videoRef.current === null) {
        stopCamera();
        return;
      }
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
      } catch {
        // Autoplay can be refused by browser policy even after getUserMedia already granted camera
        // access — the <video> element still renders the live stream once the user interacts with
        // the page in any way, so this is not treated as a hard failure.
      }
      intervalId = setInterval(() => {
        void attemptDecode();
      }, SCAN_INTERVAL_MS);
    }

    void start();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [t]);

  // A genuine success releases the camera for good; a failed submission resumes the still-running
  // camera loop instead of leaving it dead behind an error screen — see `stopCameraRef`/
  // `resumeScanningRef`'s own comments above for why this lives in its own effect rather than
  // inside the camera effect's own body (which must not restart on every submission attempt).
  useEffect(() => {
    if (submit.isSuccess) {
      stopCameraRef.current();
    } else if (submit.isError) {
      resumeScanningRef.current();
    }
  }, [submit.isSuccess, submit.isError]);

  if (cameraError !== null) {
    return <KioskShell title={t('cameraErrorTitle')} body={cameraError} />;
  }

  if (submit.isSuccess) {
    return <KioskShell title={t('doneTitle')} body={t('doneBody')} />;
  }

  // A dead token needs a fresh link, not a retry — these two stay full-screen terminal states,
  // same as `cameraError`/`isSuccess` above, since there is no live camera to resume scanning
  // against once the token itself is the problem.
  const tokenErrorCode =
    submit.isError && submit.error instanceof ApiError ? submit.error.code : null;
  if (tokenErrorCode === 'license_scan_token_expired') {
    return <KioskShell title={t('expiredTitle')} body={t('expiredBody')} />;
  }
  if (tokenErrorCode === 'license_scan_token_invalid') {
    return <KioskShell title={t('invalidTitle')} body={t('invalidBody')} />;
  }

  // Every other case — actively scanning, a submission in flight, or a submission that failed for
  // a reason a retry can plausibly fix (a network blip) — renders the same tree, with the video
  // element continuously mounted throughout. `resumeScanningRef`'s own comment explains why: this
  // element must never unmount and remount across an error, since a fresh `<video>` node would have
  // no `srcObject` and there would be nothing left to reattach it to.
  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-2xl flex-1 flex-col justify-center p-4 sm:p-8"
    >
      <div className="rounded-panel border border-line bg-surface p-4 shadow-panel sm:p-8">
        <h1 className="m-0 mb-2 text-xl font-semibold text-ink sm:text-2xl">
          {submit.isError ? t('submitErrorTitle') : t('scanningTitle')}
        </h1>
        <p className="mt-0 mb-4 text-base text-ink-muted">
          {submit.isError
            ? t('submitErrorBody')
            : submit.isPending
              ? t('submittingBody')
              : t('scanningBody')}
        </p>
        <div className="relative overflow-hidden rounded-control border border-line bg-canvas">
          {/* `muted` and `playsInline` are both required for a live-preview <video> to autoplay on
              iOS Safari without the browser's own fullscreen video controls taking over. */}
          <video ref={videoRef} muted playsInline className="aspect-video w-full object-cover" />
          {submit.isPending ? (
            <div className="absolute inset-0 flex items-center justify-center bg-canvas/70">
              <p className="m-0 rounded-control bg-surface px-3 py-2 text-sm font-medium text-ink shadow-panel">
                {t('submittingBody')}
              </p>
            </div>
          ) : null}
        </div>
        {submit.isError ? (
          <div className="mt-4 flex flex-col gap-3">
            <ErrorText message={errorMessage(submit.error)} />
            <Button
              onClick={() => {
                submit.reset();
              }}
            >
              {t('tryAgainButton')}
            </Button>
          </div>
        ) : (
          <p className="mt-4 mb-0 text-sm text-ink-muted">{t('scanningHint')}</p>
        )}
      </div>
    </main>
  );
}
